// Phone numbers in international (E.164) form. Staff type what the customer says ("050 123 4567");
// the app adds the country code of the branch they work at.
import { e164Schema } from '@ecobox/schema';

/** Country calling codes for the countries EcoBox works in, by sector code. */
export const DIAL_CODES: Readonly<Record<string, string>> = {
  UAE: '971',
  KSA: '966',
  QA: '974',
  QAT: '974',
  BH: '973',
  KW: '965',
  OM: '968',
  PH: '63',
  IN: '91',
};

export function dialCodeFor(sectorId: string | undefined): string | undefined {
  return sectorId ? DIAL_CODES[sectorId] : undefined;
}

export type PhoneResult = { ok: true; e164: string } | { ok: false; error: string };

/** "050 123 4567" with dial code 966 → +966501234567. A number that starts with + or 00 is kept as typed. */
export function normalizePhone(input: string, dial?: string): PhoneResult {
  const cleaned = input.trim().replace(/[\s\-.()]/g, '');
  if (cleaned === '') return { ok: false, error: 'Enter a phone number' };
  if (!/^\+?\d+$/.test(cleaned)) return { ok: false, error: 'Use digits only, like 050 123 4567' };
  let e164: string;
  if (cleaned.startsWith('+')) e164 = cleaned;
  else if (cleaned.startsWith('00')) e164 = `+${cleaned.slice(2)}`;
  else if (dial) {
    const local = cleaned.replace(/^0+/, '');
    // Already starts with the country code and is too long to be a local number.
    e164 =
      local.startsWith(dial) && local.length >= dial.length + 8 ? `+${local}` : `+${dial}${local}`;
  } else {
    return { ok: false, error: 'Start with the country code, like +966…' };
  }
  const checked = e164Schema.safeParse(e164);
  return checked.success
    ? { ok: true, e164 }
    : { ok: false, error: 'That does not look like a full phone number' };
}

/** +966501234567 → "+966 501234567". Unknown country codes are shown as they are. */
export function formatPhone(e164: string): string {
  const code = Object.values(DIAL_CODES)
    .sort((a, b) => b.length - a.length)
    .find((c) => e164.startsWith(`+${c}`));
  return code ? `+${code} ${e164.slice(code.length + 1)}` : e164;
}

/** Could this search text be a phone number (as opposed to a name)? */
export function looksLikePhone(text: string): boolean {
  const t = text.trim();
  return /^[+\d][\d\s\-.()]*$/.test(t) && t.replace(/\D/g, '').length >= 4;
}
